import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Page } from '../../components/Layout'
import { Login } from '../../components/Login'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import { brl, dataBR, statusLabel } from '../../lib/format'

interface CasoInterno {
  id: string
  status: string
  empreendimento: string | null
  construtora: string | null
  valor_entrada: number | null
  created_at: string
}

// Painel interno (RLS dá tudo p/ staff). Fila de casos por status.
export default function FilaCasos() {
  const { user, loading } = useAuth()
  const [casos, setCasos] = useState<CasoInterno[]>([])
  const [isStaff, setIsStaff] = useState<boolean | null>(null)

  useEffect(() => {
    if (loading || !user) return
    ;(async () => {
      const { data: perfil } = await supabase.from('perfis').select('papel').eq('user_id', user.id).maybeSingle()
      const staff = perfil?.papel === 'staff'
      setIsStaff(staff)
      if (staff) {
        const { data } = await supabase
          .from('casos')
          .select('id, status, empreendimento, construtora, valor_entrada, created_at')
          .order('created_at', { ascending: false })
        setCasos((data as CasoInterno[]) ?? [])
      }
    })()
  }, [loading, user])

  if (loading) return <Page><p className="text-pfa-slate">Carregando…</p></Page>
  if (!user) return <Page><Login staff /></Page>
  if (isStaff === false)
    return (
      <Page>
        <div className="card">
          <p className="text-pfa-slate">Área restrita à equipe.</p>
        </div>
      </Page>
    )

  const porStatus = casos.reduce<Record<string, number>>((acc, c) => {
    acc[c.status] = (acc[c.status] ?? 0) + 1
    return acc
  }, {})

  return (
    <Page>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-pfa-navy">Fila de casos</h1>
        <div className="flex gap-3">
          <Link to="/interno/producao" className="btn-ghost">Produção</Link>
          <Link to="/interno/metricas" className="btn-ghost">Métricas</Link>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {Object.entries(porStatus).map(([s, n]) => (
          <span key={s} className="rounded-full bg-pfa-navy/10 px-3 py-1 text-xs text-pfa-slate">
            {statusLabel(s)}: <strong>{n}</strong>
          </span>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-pfa-slate/70">
            <tr>
              <th className="py-2">Empreendimento</th>
              <th className="py-2">Construtora</th>
              <th className="py-2">Status</th>
              <th className="py-2">Entrada</th>
              <th className="py-2">Criado</th>
            </tr>
          </thead>
          <tbody>
            {casos.map((c) => (
              <tr key={c.id} className="border-t border-pfa-navy/10">
                <td className="py-2">
                  <Link to={`/interno/caso/${c.id}`} className="text-pfa-navy underline hover:text-pfa-coral">
                    {c.empreendimento ?? 'abrir'}
                  </Link>
                </td>
                <td className="py-2">{c.construtora ?? '—'}</td>
                <td className="py-2">{statusLabel(c.status)}</td>
                <td className="py-2">{brl(c.valor_entrada)}</td>
                <td className="py-2">{dataBR(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {casos.length === 0 && <p className="mt-4 text-pfa-slate">Nenhum caso ainda.</p>}
      </div>
    </Page>
  )
}
